/**
 * Menu de ações fiscais da venda (relacao-vendas).
 */

import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Eye, RefreshCw, Download, Receipt, XCircle, FileText, Ban, Send, Undo2, PenLine } from 'lucide-react'
import {
  getVendaDocumentoFiscal,
  VENDA_DOC_TIPO,
  canEmitirNotaFiscal,
  canEmitirDevolucao,
  canRetryDevolucaoFiscal
} from '../../../utils/vendaFiscalDocument'
import '../NFe/NFeDropdownMenu.css'

function VendaFiscalDropdownMenu({ venda, onAction, onClose, buttonRef, canReturnNfe = false }) {
  const menuRef = useRef(null)
  const doc = getVendaDocumentoFiscal(venda)
  const saleCancelled = venda?.status === 'cancelled'

  useEffect(() => {
    const updatePosition = () => {
      if (!menuRef.current || !buttonRef) return
      const rect = buttonRef.getBoundingClientRect()
      const menuWidth = 220
      let left = rect.right - menuWidth
      left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8))
      const top = Math.min(rect.bottom + 8, window.innerHeight - 16)
      menuRef.current.style.top = `${top}px`
      menuRef.current.style.left = `${left}px`
      menuRef.current.style.visibility = 'visible'
    }

    updatePosition()
    const raf = requestAnimationFrame(updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    const handleClickOutside = (event) => {
      if (menuRef.current?.contains(event.target)) return
      if (buttonRef?.contains(event.target)) return
      onClose()
    }

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 150)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
      document.removeEventListener('mousedown', handleClickOutside)
      clearTimeout(timeoutId)
    }
  }, [buttonRef, onClose])

  const handleAction = (action) => {
    onAction(action, venda, doc)
    onClose()
  }

  const nfeStatus = (doc.status || '').toLowerCase()
  const nfceStatus = (doc.status || venda?.status_nfe || '').toLowerCase()

  const canConsultarNfe =
    doc.tipo === VENDA_DOC_TIPO.NFE &&
    ['saved', 'transmitted', 'error', 'authorized', 'processando'].includes(nfeStatus)

  const canVisualizarNotaNfe =
    doc.tipo === VENDA_DOC_TIPO.NFE &&
    (['transmitted', 'authorized', 'saved'].includes(nfeStatus) ||
      Boolean(venda?.nfe_url_pdf || doc.urlPdf))

  const canCancelarNfe =
    doc.tipo === VENDA_DOC_TIPO.NFE &&
    !saleCancelled &&
    ['saved', 'transmitted', 'authorized'].includes(nfeStatus)

  const canConsultarNfce =
    doc.tipo === VENDA_DOC_TIPO.NFCE && !!(venda?.chave_nfe || venda?.referencia_nfe)

  const canVisualizarNotaNfce =
    doc.tipo === VENDA_DOC_TIPO.NFCE &&
    (nfceStatus === 'autorizado' ||
      nfceStatus === 'authorized' ||
      !!doc.urlPdf ||
      !!venda?.chave_nfe)

  const canCancelarNfce =
    doc.tipo === VENDA_DOC_TIPO.NFCE &&
    !saleCancelled &&
    (nfceStatus === 'autorizado' || nfceStatus === 'authorized')

  const canVisualizarCupom =
    doc.tipo === VENDA_DOC_TIPO.CUPOM || doc.tipo === VENDA_DOC_TIPO.NENHUM

  const showEmitirNotaFiscal = canEmitirNotaFiscal(venda)
  const showEmitirDevolucao = canReturnNfe && canEmitirDevolucao(venda)
  const showRetryDevolucao = canReturnNfe && canRetryDevolucaoFiscal(venda)

  const nfeAlreadyCancelled = doc.tipo === VENDA_DOC_TIPO.NFE && nfeStatus === 'cancelled'
  const canCancelarVenda =
    !saleCancelled &&
    (nfeAlreadyCancelled || doc.tipo !== VENDA_DOC_TIPO.NFE) &&
    !(doc.tipo === VENDA_DOC_TIPO.NFCE && canCancelarNfce)

  const menuContent = (
    <div
      ref={menuRef}
      className="nfe-dropdown-menu-fixed venda-fiscal-dropdown-menu"
      style={{ visibility: buttonRef ? 'visible' : 'hidden' }}
    >
      <button type="button" onClick={() => handleAction('view')}>
        <Eye size={16} />
        Visualizar
      </button>

      {canConsultarNfe && (
        <button type="button" onClick={() => handleAction('consultar-status')}>
          <RefreshCw size={16} />
          Consultar status
        </button>
      )}

      {canConsultarNfce && (
        <button type="button" onClick={() => handleAction('consultar-status')}>
          <RefreshCw size={16} />
          Consultar status
        </button>
      )}

      {canVisualizarNotaNfe && (
        <button type="button" onClick={() => handleAction('visualizar-danfe')}>
          <Download size={16} />
          Visualizar DANFE (NF-e)
        </button>
      )}

      {canVisualizarNotaNfe && venda?.customer_id && (
        <button type="button" onClick={() => handleAction('corrigir-razao-social')}>
          <PenLine size={16} />
          Corrigir razão social
        </button>
      )}

      {canVisualizarNotaNfce && (
        <button type="button" onClick={() => handleAction('visualizar-nfce')}>
          <Receipt size={16} />
          Visualizar cupom (NFC-e)
        </button>
      )}

      {canVisualizarCupom && (
        <>
          <button type="button" onClick={() => handleAction('visualizar-cupom')}>
            <FileText size={16} />
            Visualizar cupom
          </button>
          <button type="button" onClick={() => handleAction('download-cupom')}>
            <Download size={16} />
            Download cupom
          </button>
        </>
      )}

      {showEmitirNotaFiscal && (
        <button type="button" onClick={() => handleAction('emitir-nota-fiscal')}>
          <Send size={16} />
          Emitir nota fiscal
        </button>
      )}

      {showEmitirDevolucao && (
        <button type="button" onClick={() => handleAction('emitir-devolucao')}>
          <Undo2 size={16} />
          Emitir devolução
        </button>
      )}

      {showRetryDevolucao && (
        <button type="button" onClick={() => handleAction('retry-devolucao-fiscal')}>
          <RefreshCw size={16} />
          Reenviar nota fiscal
        </button>
      )}

      {canCancelarNfe && (
        <button type="button" className="danger" onClick={() => handleAction('cancelar-nfe')}>
          <XCircle size={16} />
          Cancelar NF-e
        </button>
      )}

      {canCancelarNfce && (
        <button type="button" className="danger" onClick={() => handleAction('cancelar-nfce')}>
          <XCircle size={16} />
          Cancelar NFC-e
        </button>
      )}

      {canCancelarVenda && (
        <button type="button" className="danger" onClick={() => handleAction('cancelar-venda')}>
          <Ban size={16} />
          Cancelar venda
        </button>
      )}
    </div>
  )

  return typeof document !== 'undefined' ? createPortal(menuContent, document.body) : menuContent
}

export default VendaFiscalDropdownMenu
